import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { KeyRound, Lock, CheckCircle2, XCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import logo from '../assets/logo-asistelo.png';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/verify-reset-token/${token}`);
      setTokenValid(true);
      setEmail(response.data.email);
    } catch (error) {
      setTokenValid(false);
      toast.error('Token inválido o expirado');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Contraseña actualizada exitosamente');
      
      // Redirigir al login después de 3 segundos
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al resetear contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center shadow-lg border border-slate-200">
          <div className="flex justify-center mb-6">
            <img src={logo} alt="Asistelo" className="h-12 w-auto" />
          </div>
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-[#2537b0]/10 rounded-full flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-[#2537b0] animate-spin" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Verificando...
          </h1>
          <p className="text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
            Por favor espera mientras verificamos tu solicitud.
          </p>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center shadow-lg border border-slate-200">
          <div className="flex justify-center mb-6">
            <img src={logo} alt="Asistelo" className="h-12 w-auto" />
          </div>
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Enlace Inválido o Expirado
          </h1>
          <p className="text-slate-600 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
            Este enlace de restablecimiento no es válido o ha expirado.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm font-semibold text-amber-900 mb-2">
              ⚠️ Posibles causas:
            </p>
            <ul className="text-sm text-amber-800 space-y-2">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>El enlace expiró (válido por <strong>15 minutos</strong>)</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>El enlace ya fue utilizado</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>El enlace es inválido</span>
              </li>
            </ul>
          </div>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/forgot-password')}
              className="w-full bg-[#2537b0] hover:bg-[#1c2a8a]"
            >
              Solicitar Nuevo Enlace
            </Button>
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              className="w-full"
            >
              Volver al Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center shadow-lg border border-slate-200">
          <div className="flex justify-center mb-6">
            <img src={logo} alt="Asistelo" className="h-12 w-auto" />
          </div>
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            ¡Contraseña Actualizada!
          </h1>
          <p className="text-slate-600 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
            Tu contraseña ha sido restablecida exitosamente.
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-green-800 font-medium">
              ✅ Serás redirigido al login en unos segundos...
            </p>
          </div>
          <Button
            onClick={() => navigate('/login')}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            Ir al Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 shadow-lg border border-slate-200">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img src={logo} alt="Asistelo" className="h-12 w-auto" />
        </div>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-[#2537b0]/10 rounded-full flex items-center justify-center">
            <KeyRound className="w-10 h-10 text-[#2537b0]" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-slate-900 mb-3 text-center" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Restablecer Contraseña
        </h1>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
          <p className="text-sm text-blue-900 text-center">
            Restableciendo para: <strong>{email}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Nueva Contraseña
            </Label>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Mínimo 6 caracteres"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="confirmPassword" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Confirmar Contraseña
            </Label>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Repite tu contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10 pr-10"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-slate-700 mb-2">
              📋 Requisitos de contraseña:
            </p>
            <ul className="text-xs text-slate-600 space-y-1">
              <li className="flex items-center">
                <span className={`mr-2 ${password.length >= 6 ? 'text-green-600' : 'text-slate-400'}`}>
                  {password.length >= 6 ? '✓' : '○'}
                </span>
                Mínimo 6 caracteres
              </li>
              <li className="flex items-center">
                <span className={`mr-2 ${password && confirmPassword && password === confirmPassword ? 'text-green-600' : 'text-slate-400'}`}>
                  {password && confirmPassword && password === confirmPassword ? '✓' : '○'}
                </span>
                Ambas contraseñas coinciden
              </li>
            </ul>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#2537b0] hover:bg-[#1c2a8a] py-3 font-medium"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Actualizando...
              </>
            ) : (
              'Restablecer Contraseña'
            )}
          </Button>
        </form>

        <div className="mt-6 pt-6 border-t border-slate-200">
          <Button
            onClick={() => navigate('/login')}
            variant="ghost"
            className="w-full text-slate-600 hover:text-slate-900"
          >
            Volver al Login
          </Button>
        </div>
      </Card>
    </div>
  );
};
